import { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { useCurrentUserContext } from "../hooks/use-current-user-context";

export enum FontSizeOption {
  small = "small",
  medium = "medium",
  large = "large",
}

export enum FontSizeInRem {
  small = 0.875,
  medium = 1,
  large = 1.125,
}

export enum AnimationPreference {
  enabled = "enabled",
  disabled = "disabled",
}

interface AppearanceContextType {
  fontSize: FontSizeOption;
  setFontSize: (size: FontSizeOption) => void;
  animationsEnabled: boolean;
  setAnimationsEnabled: (enabled: boolean) => void;
}

const FONT_SIZE_KEY = "USER_FONT_SIZE";
const ANIMATIONS_ENABLED_KEY = "USER_ANIMATIONS_ENABLED";

const defaultValue: AppearanceContextType = {
  fontSize: FontSizeOption.medium,
  setFontSize: () => {},
  animationsEnabled: true,
  setAnimationsEnabled: () => {},
};

const AppearanceContext = createContext<AppearanceContextType>(defaultValue);

export const AppearanceProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useCurrentUserContext?.() || {};
  const [fontSize, setFontSizeState] = useState<FontSizeOption>(FontSizeOption.medium);
  const [animationsEnabled, setAnimationsEnabledState] = useState<boolean>(true);

  useEffect(() => {
    if (currentUser?.appearance) {
      try {
        const appearance = JSON.parse(currentUser.appearance);
        if (appearance.fontSize) {
          setFontSizeState(appearance.fontSize);
        }
        if (typeof appearance.animationsEnabled === "boolean") {
          setAnimationsEnabledState(appearance.animationsEnabled);
        }
      } catch (error) {
        console.warn("Failed to parse user appearance:", error);
      }
    } else {
      const storedFontSize = localStorage.getItem(FONT_SIZE_KEY) as FontSizeOption | null;
      if (storedFontSize && Object.values(FontSizeOption).includes(storedFontSize)) {
        setFontSizeState(storedFontSize);
      }
      const storedAnimations = localStorage.getItem(ANIMATIONS_ENABLED_KEY);
      if (storedAnimations !== null) {
        setAnimationsEnabledState(storedAnimations === "true");
      }
    }
  }, [currentUser]);

  useEffect(() => {
    localStorage.setItem(FONT_SIZE_KEY, fontSize);
    document.documentElement.classList.remove("font-size-small", "font-size-medium", "font-size-large");
    document.documentElement.classList.add(`font-size-${fontSize}`);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem(ANIMATIONS_ENABLED_KEY, animationsEnabled.toString());
    if (animationsEnabled) {
      document.documentElement.classList.remove("no-animations");
    } else {
      document.documentElement.classList.add("no-animations");
    }
  }, [animationsEnabled]);

  const setFontSize = (size: FontSizeOption) => {
    setFontSizeState(size);
    // Backend sync will be handled in the selector
  };

  const setAnimationsEnabled = (enabled: boolean) => {
    setAnimationsEnabledState(enabled);
    // Backend sync will be handled in the selector
  };

  return (
    <AppearanceContext.Provider
      value={{
        fontSize,
        setFontSize,
        animationsEnabled,
        setAnimationsEnabled,
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => useContext(AppearanceContext);
